import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, ArrowRight, Loader2, BookOpen, Video, Package, Check, ChevronDown, ChevronRight, Copy, Edit2, Save, X, RefreshCw, Download, FileText, Sparkles, Image, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

import monetizationLogo from "@/assets/monetization-logo-new.png";
import { CourseDeliveryOptions } from "@/components/monetization/CourseDeliveryOptions";
import { ProductCharter } from "@/components/monetization/ProductCharter";
import { useCredits } from "@/hooks/useCredits";
import { MonetizationStepper } from "@/components/monetization/MonetizationStepper";

interface ProductContent {
  titles?: { main: string; subtitle?: string; transformation?: string }[];
  positioning?: string;
  targetReader?: any;
  targetStudent?: any;
  transformation?: string;
  chapters?: any[];
  modules?: any[];
  assets?: any[];
  totalWordCount?: number;
  estimatedPages?: number;
  totalLessons?: number;
  totalDuration?: string;
  bonuses?: string[];
  callToActions?: string[];
  upsells?: string[];
  packNames?: string[];
  tagline?: string;
  targetUser?: any;
  useCases?: string[];
  folderStructure?: string[];
  namingConvention?: string;
  deliveryFormat?: string;
  totalAssets?: number;
}

// Helper to convert target audience (could be string or object) to displayable string
const formatTargetAudience = (target: any): string => {
  if (!target) return "";
  if (typeof target === "string") return target;
  if (typeof target === "object") {
    const parts: string[] = [];
    if (target.whoTheyAre) parts.push(target.whoTheyAre);
    if (target.struggles) parts.push(`Struggles: ${target.struggles}`);
    if (target.goals) parts.push(`Goals: ${target.goals}`);
    return parts.join(" | ") || JSON.stringify(target);
  }
  return String(target);
};

// Helper to safely render list items that sometimes arrive as objects
const formatListItem = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const name = typeof value.name === "string" ? value.name : "";
    const description = typeof value.description === "string" ? value.description : "";
    if (name && description) return `${name} — ${description}`;
    if (name) return name;
    if (description) return description;
    return JSON.stringify(value);
  }
  return String(value);
};

// Security: Sanitize content for PDF generation to prevent path traversal attacks
// Also strips emoji/unicode that jsPDF's Helvetica font can't render
const sanitizeForPDF = (text: string | null | undefined): string => {
  if (!text) return "";
  if (typeof text !== "string") return String(text);
  
  // Remove path traversal patterns
  let sanitized = text.replace(/\.\.\/|\.\.\\|\.\.%2[fF]|\.\.%5[cC]/g, '');
  // Remove null bytes and control characters (except newlines and tabs for formatting)
  sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  // Remove potential file:// protocol references
  sanitized = sanitized.replace(/file:\/\//gi, '');
  // Strip emoji and other non-Latin Unicode that Helvetica can't render
  // Keep basic Latin, Latin-1 Supplement, and common punctuation
  sanitized = sanitized.replace(/[^\x20-\x7E\xA0-\xFF\n\t\r]/g, '');
  
  return sanitized;
};

interface GeneratedImage {
  description: string;
  imageData: string;
  chapterIndex: number;
}

function CourseHostingGuidance() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-6 rounded-lg border-l-4 border-amber-500/70" style={{ background: 'rgba(245, 166, 35, 0.06)', border: '1px solid rgba(245, 166, 35, 0.15)', borderLeft: '4px solid rgba(245, 166, 35, 0.7)' }}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full px-5 py-4 flex items-center gap-2 text-left hover:bg-amber-500/5 transition-colors rounded-lg">
          <span className="text-amber-400 text-lg">ℹ️</span>
          <span className="font-medium text-sm text-foreground">Where do I host this?</span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-5 pb-5 space-y-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Where to host your course — free options</p>
            <p>Your course outline and content are ready. To deliver it to your students, you'll need a hosting platform. Here are the best free options:</p>
            
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-foreground">Gumroad (Recommended)</p>
                <p>Upload your course content as files or embed video links. Free plan available. No monthly fee — Gumroad takes a small % per sale. Best for beginners. → gumroad.com</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Teachable</p>
                <p>Host up to 10 students for free on their free plan. Supports video, quizzes, and certificates. → teachable.com</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">Notion (Free, no limit)</p>
                <p>Organise your course as a Notion page and share a private link with paying customers. Works best for text-based or PDF courses. → notion.so</p>
              </div>
              <div>
                <p className="font-semibold text-foreground">YouTube (Unlisted videos)</p>
                <p>Upload your lesson videos to YouTube as Unlisted (not public). Only people with the link can watch. Share the links inside your Gumroad or Notion course page. Free, unlimited storage.</p>
              </div>
            </div>
            
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm"><span className="font-semibold text-amber-400">Tip:</span> The fastest setup is YouTube (Unlisted) for videos + Gumroad for delivery and payment. You can be live in under 2 hours.</p>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export default function MonetizationBuild() {
  const { projectId, productId } = useParams();
  const navigate = useNavigate();
  const { consume } = useCredits();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [product, setProduct] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [content, setContent] = useState<ProductContent | null>(null);
  const [expandedChapter, setExpandedChapter] = useState<number | null>(null);
  const [generatingChapter, setGeneratingChapter] = useState<number | null>(null);
  const [chapterContent, setChapterContent] = useState<Record<number, string>>({});
  const [editingChapter, setEditingChapter] = useState<number | null>(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [generatingImages, setGeneratingImages] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  
  // Course script generation state
  const [scriptTone, setScriptTone] = useState<string>("");
  const [customToneText, setCustomToneText] = useState<string>("");
  const [lessonScripts, setLessonScripts] = useState<Record<string, string>>({});
  const [generatingLesson, setGeneratingLesson] = useState<string | null>(null);
  const [generatingAllLessons, setGeneratingAllLessons] = useState(false);
  const [lessonProgress, setLessonProgress] = useState(0);
  const [expandedModule, setExpandedModule] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"product" | "charter">("product");

  useEffect(() => {
    if (projectId && productId) {
      fetchData();
    }
  }, [projectId, productId]);

  const fetchData = async () => {
    try {
      const [projectRes, productRes] = await Promise.all([
        supabase.from("monetization_projects").select("*").eq("id", projectId).single(),
        supabase.from("monetization_products").select("*").eq("id", productId).single(),
      ]);

      if (projectRes.error) throw projectRes.error;
      if (productRes.error) throw productRes.error;

      setProject(projectRes.data);
      setProduct(productRes.data);

      // Check if content already exists
      if (productRes.data.content && typeof productRes.data.content === 'object' && Object.keys(productRes.data.content).length > 1) {
        const existingContent = productRes.data.content as ProductContent;
        setContent(existingContent);
        
        // Load any saved chapter content (for eBooks)
        if (existingContent.chapters) {
          const chapters = existingContent.chapters || [];
          const savedContent: Record<number, string> = {};
          chapters.forEach((ch: any, i: number) => {
            if (ch.fullContent) {
              savedContent[i] = ch.fullContent;
            }
          });
          setChapterContent(savedContent);
        }
        
        // Load saved lesson scripts and tone (for courses)
        if ((existingContent as any).scriptTone) {
          setScriptTone((existingContent as any).scriptTone);
        }
        if ((existingContent as any).customToneText) {
          setCustomToneText((existingContent as any).customToneText);
        }
        if ((existingContent as any).lessonScripts) {
          setLessonScripts((existingContent as any).lessonScripts);
        }
      } else {
        await generateContent(productRes.data, projectRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const generateContent = async (productData: any, projectData: any) => {
    setGenerating(true);
    try {
      let type = "generate-ebook";
      if (productData.product_type === "video_course" || productData.product_type === "mini_course") {
        type = "generate-course";
      } else if (productData.product_type === "toolkit") {
        type = "generate-toolkit";
      } else if (productData.product_type === "asset_pack") {
        type = "generate-asset-pack";
      } else if (productData.product_type === "notion_template") {
        type = "generate-notion-template";
      } else if (productData.product_type === "swipe_file") {
        type = "generate-swipe-file";
      } else if (productData.product_type === "newsletter_pack") {
        type = "generate-newsletter-pack";
      } else if (productData.product_type === "challenge_kit") {
        type = "generate-challenge-kit";
      } else if (productData.product_type === "resource_bundle") {
        type = "generate-resource-bundle";
      } else if (productData.product_type === "content_calendar") {
        type = "generate-content-calendar";
      } else if (productData.product_type === "workbook") {
        type = "generate-workbook";
      } else if (productData.product_type === "coaching_scripts") {
        type = "generate-coaching-scripts";
      }

      // For asset packs, include user-uploaded asset info
      const existingContent = productData.content || {};
      const userAssetPacks = existingContent.userAssetPacks || [];

      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type,
          params: {
            productName: productData.title,
            niche: projectData.niche,
            audience: projectData.audience_type,
            description: productData.content?.description,
            // Pass user-uploaded asset packs for asset pack products
            userAssetPacks: userAssetPacks.length > 0 ? userAssetPacks.map((pack: any) => ({
              name: pack.name,
              description: pack.description,
              fileCount: pack.files?.length || 0,
              fileNames: pack.files?.map((f: any) => f.name) || [],
            })) : undefined,
          },
        },
      });

      if (error) throw error;

      setContent(data);

      // Save to database, preserving userAssetPacks
      await supabase
        .from("monetization_products")
        .update({ 
          content: { ...productData.content, ...data },
          status: "generating"
        })
        .eq("id", productId);

    } catch (error) {
      console.error("Error generating content:", error);
      toast.error("Failed to generate product content");
    } finally {
      setGenerating(false);
    }
  };

  const generateChapterContent = async (chapterIndex: number) => {
    if (!content?.chapters || !product) return;
    
    const chapter = content.chapters[chapterIndex];
    setGeneratingChapter(chapterIndex);

    try {
      const credited = await consume("monetization-build-chapter", "Generate chapter content");
      if (!credited) { setGeneratingChapter(null); return; }
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "generate-ebook-chapter",
          params: {
            chapterNumber: chapter.number,
            chapterTitle: chapter.title,
            chapterDescription: chapter.description,
            keyPoints: chapter.keyPoints,
            wordCount: chapter.wordCount,
            bookTitle: content.titles?.[0]?.main || product.title,
            audience: project?.audience_type,
          },
        },
      });

      if (error) throw error;

      // The response is plain text markdown
      const generatedText = data.content || data;
      
      setChapterContent((prev) => ({
        ...prev,
        [chapterIndex]: typeof generatedText === 'string' ? generatedText : JSON.stringify(generatedText),
      }));

      // Save to database
      const updatedChapters = [...content.chapters];
      updatedChapters[chapterIndex] = {
        ...chapter,
        fullContent: typeof generatedText === 'string' ? generatedText : JSON.stringify(generatedText),
      };
      
      await supabase
        .from("monetization_products")
        .update({ 
          content: { ...content, chapters: updatedChapters }
        })
        .eq("id", productId);

      return generatedText;
    } catch (error) {
      console.error("Error generating chapter:", error);
      toast.error("Failed to generate chapter content");
      throw error;
    } finally {
      setGeneratingChapter(null);
    }
  };

  const generateAllContent = async () => {
    if (!content?.chapters) return;

    setGeneratingAll(true);
    setGenerationProgress(0);
    const totalChapters = content.chapters.length;
    let completedChapters = 0;

    toast.info(`Generating all ${totalChapters} chapters. This may take a few minutes...`);

    for (let i = 0; i < totalChapters; i++) {
      // Skip if already generated
      if (chapterContent[i]) {
        completedChapters++;
        setGenerationProgress((completedChapters / totalChapters) * 100);
        continue;
      }

      try {
        await generateChapterContent(i);
        completedChapters++;
        setGenerationProgress((completedChapters / totalChapters) * 100);
        
        // Small delay to avoid rate limiting
        if (i < totalChapters - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`Failed to generate chapter ${i + 1}:`, error);
        toast.error(`Failed to generate chapter ${i + 1}. You can retry individual chapters.`);
      }
    }

    setGeneratingAll(false);
    toast.success("All content generated! You can now generate images or export your PDF.");
  };

  const generateDiagramImage = async (description: string, context: string, chapterIndex: number): Promise<GeneratedImage | null> => {
    try {
      // Determine the type based on description
      let type = "diagram";
      if (description.toLowerCase().includes("infographic")) {
        type = "infographic";
      } else if (description.toLowerCase().includes("flowchart") || description.toLowerCase().includes("process")) {
        type = "flowchart";
      } else if (description.toLowerCase().includes("map") || description.toLowerCase().includes("concept")) {
        type = "concept-map";
      }

      const { data, error } = await supabase.functions.invoke("generate-diagram", {
        body: {
          description,
          context,
          type,
        },
      });

      if (error) throw error;

      if (data?.image) {
        return {
          description,
          imageData: data.image,
          chapterIndex,
        };
      }
      return null;
    } catch (error) {
      console.error("Error generating diagram:", error);
      return null;
    }
  };

  const extractVisualDescriptions = (text: string): string[] => {
    const patterns = [
      /\[IMAGE:\s*([^\]]+)\]/gi,
      /\[DIAGRAM:\s*([^\]]+)\]/gi,
      /\[INFOGRAPHIC:\s*([^\]]+)\]/gi,
      /\[FLOWCHART:\s*([^\]]+)\]/gi,
      /\[VISUAL:\s*([^\]]+)\]/gi,
    ];
    
    const descriptions: string[] = [];
    for (const pattern of patterns) {
      const matches = text.matchAll(pattern);
      for (const match of matches) {
        if (match[1]) {
          descriptions.push(match[1].trim());
        }
      }
    }
    return descriptions;
  };

  const generateAllImages = async () => {
    if (!content?.chapters) return;

    setGeneratingImages(true);
    setImageProgress(0);
    const allVisuals: { description: string; context: string; chapterIndex: number }[] = [];

    // Collect all visual descriptions from chapter content
    content.chapters.forEach((chapter, i) => {
      const chapterText = chapterContent[i] || "";
      const visuals = extractVisualDescriptions(chapterText);
      
      // Also check suggested visuals from the chapter structure
      if (chapter.suggestedVisuals) {
        visuals.push(...chapter.suggestedVisuals);
      }

      visuals.forEach(desc => {
        allVisuals.push({
          description: desc,
          context: `Chapter ${chapter.number}: ${chapter.title} - ${chapter.description || ""}`,
          chapterIndex: i,
        });
      });
    });

    if (allVisuals.length === 0) {
      toast.info("No visual placeholders found in the content. Generate chapter content first.");
      setGeneratingImages(false);
      return;
    }

    toast.info(`Generating ${allVisuals.length} images. This may take a few minutes...`);
    
    const newImages: GeneratedImage[] = [];
    let completed = 0;

    for (const visual of allVisuals) {
      try {
        const image = await generateDiagramImage(visual.description, visual.context, visual.chapterIndex);
        if (image) {
          newImages.push(image);
        }
        completed++;
        setImageProgress((completed / allVisuals.length) * 100);
        
        // Add delay to avoid rate limiting
        if (completed < allVisuals.length) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error(`Failed to generate image: ${visual.description}`, error);
        completed++;
        setImageProgress((completed / allVisuals.length) * 100);
      }
    }

    setGeneratedImages(prev => [...prev, ...newImages]);
    setGeneratingImages(false);
    toast.success(`Generated ${newImages.length} images! You can now export your PDF with visuals.`);
  };

  // Course lesson script generation
  const generateLessonScript = async (moduleIndex: number, lessonIndex: number, lesson: any, module: any) => {
    if (!scriptTone) {
      toast.error("Please select a script tone first");
      return;
    }

    if (scriptTone === "custom" && !customToneText.trim()) {
      toast.error("Please describe your custom tone style");
      return;
    }

    const lessonKey = `${moduleIndex}-${lessonIndex}`;
    setGeneratingLesson(lessonKey);

    // Determine the actual tone to send - for custom, use the text description
    const toneToSend = scriptTone === "custom" ? `Custom style: ${customToneText}` : scriptTone;

    try {
      const { data, error } = await supabase.functions.invoke("monetization-ai", {
        body: {
          type: "generate-lesson-script",
          params: {
            moduleNumber: module.number || moduleIndex + 1,
            moduleTitle: module.title || `Module ${moduleIndex + 1}`,
            lessonNumber: lessonIndex + 1,
            lessonTitle: lesson.title || `Lesson ${lessonIndex + 1}`,
            lessonDescription: lesson.description || "",
            duration: lesson.duration || "5-10 minutes",
            audience: project?.audience_type || "general audience",
            tone: toneToSend,
            courseTitle: content?.titles?.[0]?.main || product?.title || "Untitled Course",
          },
        },
      });

      if (error) throw error;

      const generatedScript = data.content || data;
      const scriptText = typeof generatedScript === 'string' ? generatedScript : JSON.stringify(generatedScript);

      setLessonScripts((prev) => ({
        ...prev,
        [lessonKey]: scriptText,
      }));

      // Save to database
      await supabase
        .from("monetization_products")
        .update({ 
          content: { 
            ...content, 
            scriptTone,
            lessonScripts: { ...lessonScripts, [lessonKey]: scriptText }
          }
        })
        .eq("id", productId);

      return scriptText;
    } catch (error) {
      console.error("Error generating lesson script:", error);
      toast.error("Failed to generate lesson script");
      throw error;
    } finally {
      setGeneratingLesson(null);
    }
  };

  const generateAllLessonScripts = async () => {
    if (!content?.modules || !scriptTone) {
      toast.error("Please select a script tone first");
      return;
    }

    if (scriptTone === "custom" && !customToneText.trim()) {
      toast.error("Please describe your custom tone style");
      return;
    }

    setGeneratingAllLessons(true);
    setLessonProgress(0);

    const allLessons: { moduleIndex: number; lessonIndex: number; lesson: any; module: any }[] = [];
    content.modules.forEach((module, mi) => {
      module.lessons?.forEach((lesson: any, li: number) => {
        allLessons.push({ moduleIndex: mi, lessonIndex: li, lesson, module });
      });
    });

    const totalLessons = allLessons.length;
    let completedLessons = 0;

    toast.info(`Generating scripts for ${totalLessons} lessons. This may take a while...`);

    for (const { moduleIndex, lessonIndex, lesson, module } of allLessons) {
      const lessonKey = `${moduleIndex}-${lessonIndex}`;
      
      // Skip if already generated
      if (lessonScripts[lessonKey]) {
        completedLessons++;
        setLessonProgress((completedLessons / totalLessons) * 100);
        continue;
      }

      try {
        await generateLessonScript(moduleIndex, lessonIndex, lesson, module);
        completedLessons++;
        setLessonProgress((completedLessons / totalLessons) * 100);
        
        // Small delay to avoid rate limiting
        if (completedLessons < totalLessons) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      } catch (error) {
        console.error(`Failed to generate script for lesson ${lessonIndex + 1}:`, error);
        completedLessons++;
        setLessonProgress((completedLessons / totalLessons) * 100);
      }
    }

    setGeneratingAllLessons(false);
    toast.success("All lesson scripts generated!");
  };

  const saveToneSelection = async (tone: string) => {
    setScriptTone(tone);
    await supabase
      .from("monetization_products")
      .update({ 
        content: { ...content, scriptTone: tone, customToneText: tone === "custom" ? customToneText : undefined }
      })
      .eq("id", productId);
  };

  const saveCustomToneText = async (text: string) => {
    setCustomToneText(text);
    await supabase
      .from("monetization_products")
      .update({ 
        content: { ...content, scriptTone, customToneText: text }
      })
      .eq("id", productId);
  };

  const getTotalLessonCount = () => {
    if (!content?.modules) return 0;
    return content.modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
  };

  const getGeneratedLessonCount = () => {
    return Object.keys(lessonScripts).length;
  };

  const handleCopyContent = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleSaveChapter = async (chapterIndex: number) => {
    if (!content?.chapters) return;

    const updatedChapters = [...content.chapters];
    updatedChapters[chapterIndex] = {
      ...updatedChapters[chapterIndex],
      fullContent: chapterContent[chapterIndex],
    };

    await supabase
      .from("monetization_products")
      .update({ content: { ...content, chapters: updatedChapters } })
      .eq("id", productId);

    setEditingChapter(null);
    toast.success("Chapter saved");
  };

  const handleExportPDF = async () => {
    if (!product || !content) {
      toast.error("No product content to export. Please generate content first.");
      return;
    }
    try {

    // Auto-generate images if none exist yet
    if (generatedImages.length === 0) {
      const allVisuals: { description: string; context: string; chapterIndex: number }[] = [];

      // Collect from chapters (eBooks)
      if (content.chapters) {
        content.chapters.forEach((chapter: any, i: number) => {
          const chapterText = chapterContent[i] || "";
          const visuals = extractVisualDescriptions(chapterText);
          if (chapter.suggestedVisuals) {
            visuals.push(...chapter.suggestedVisuals);
          }
          visuals.forEach(desc => {
            allVisuals.push({
              description: desc,
              context: `Chapter ${chapter.number}: ${chapter.title} - ${chapter.description || ""}`,
              chapterIndex: i,
            });
          });
        });
      }

      // Collect from toolkit items
      if (product.product_type === "toolkit" && content.assets) {
        content.assets.forEach((item: any) => {
          const itemContent = item.content || item.fullContent || "";
          const visuals = extractVisualDescriptions(itemContent);
          visuals.forEach(desc => {
            allVisuals.push({
              description: desc,
              context: `${item.title || item.name || "Toolkit item"}`,
              chapterIndex: -1,
            });
          });
        });
      }

      if (allVisuals.length > 0) {
        toast.info(`Generating ${allVisuals.length} images before export. Please wait...`);
        setGeneratingImages(true);
        setImageProgress(0);
        const newImages: GeneratedImage[] = [];
        let completed = 0;

        for (const visual of allVisuals) {
          try {
            const image = await generateDiagramImage(visual.description, visual.context, visual.chapterIndex);
            if (image) newImages.push(image);
            completed++;
            setImageProgress((completed / allVisuals.length) * 100);
            if (completed < allVisuals.length) {
              await new Promise(resolve => setTimeout(resolve, 1500));
            }
          } catch {
            completed++;
            setImageProgress((completed / allVisuals.length) * 100);
          }
        }

        setGeneratedImages(prev => [...prev, ...newImages]);
        setGeneratingImages(false);
        // Use the newly generated images for the PDF below
        // We need to pass them since setState is async
        await buildPDF([...generatedImages, ...newImages]);
        return;
      }
    }

    await buildPDF(generatedImages);
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Failed to export PDF. Please try again.");
    }
  };

  const buildPDF = async (images: GeneratedImage[]) => {
    if (!product || !content) return;
    const isToolkitType = product?.product_type === "toolkit";
    try {
    
    // Load jsPDF from CDN if not available
    if (typeof window !== 'undefined' && !(window as any).jspdf) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load jsPDF library'));
        document.head.appendChild(script);
      });
    }
    const { jsPDF } = (window as any).jspdf;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPos = 20;

    const addText = (text: string, size: number, style: "normal" | "bold" | "italic" = "normal", color: [number, number, number] = [0, 0, 0]) => {
      const sanitizedText = sanitizeForPDF(text);
      doc.setFontSize(size);
      doc.setFont("helvetica", style);
      doc.setTextColor(color[0], color[1], color[2]);
      const lines = doc.splitTextToSize(sanitizedText, maxWidth);
      
      if (yPos + lines.length * (size * 0.5) > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.text(lines, margin, yPos);
      yPos += lines.length * (size * 0.5) + 3;
    };

    const addSpace = (space: number) => {
      yPos += space;
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
    };

    const addDivider = () => {
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;
    };

    const addBox = (title: string, boxContent: string, bgColor: [number, number, number] = [245, 245, 250]) => {
      const sanitizedTitle = sanitizeForPDF(title);
      const sanitizedContent = sanitizeForPDF(boxContent);
      const boxHeight = 25;
      if (yPos + boxHeight > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
      doc.roundedRect(margin, yPos - 5, maxWidth, boxHeight, 3, 3, 'F');
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(100, 100, 100);
      doc.text(sanitizedTitle.toUpperCase(), margin + 5, yPos + 3);
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 30);
      const contentLines = doc.splitTextToSize(sanitizedContent, maxWidth - 10);
      doc.text(contentLines[0], margin + 5, yPos + 12);
      
      yPos += boxHeight + 5;
    };

    // Render a markdown table row in PDF
    const addTableRow = (line: string, _pdfDoc: any, tableMargin: number, tableMaxWidth: number) => {
      const cells = line.split('|').filter(c => c.trim() !== '');
      if (cells.every(c => /^[\s\-:]+$/.test(c))) {
        doc.setDrawColor(200, 200, 200);
        doc.line(tableMargin, yPos, tableMargin + tableMaxWidth, yPos);
        yPos += 3;
        return;
      }
      if (yPos > pageHeight - 30) { doc.addPage(); yPos = 20; }
      const cellWidth = tableMaxWidth / Math.max(cells.length, 1);
      doc.setFillColor(240, 242, 245);
      doc.rect(tableMargin, yPos - 4, tableMaxWidth, 12, 'F');
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(40, 40, 40);
      cells.forEach((cell, idx) => {
        const cellText = sanitizeForPDF(cell.trim()).substring(0, 40);
        doc.text(cellText, tableMargin + idx * cellWidth + 3, yPos + 3);
      });
      yPos += 12;
    };

    const addVisualPlaceholder = (description: string, chapterIdx: number) => {
      const sanitizedDescription = sanitizeForPDF(description);
      // Try to find a generated image matching this description
      const matchingImage = images.find(
        img => (chapterIdx === -1 || img.chapterIndex === chapterIdx) &&
        (img.description.toLowerCase().includes(description.toLowerCase().substring(0, 15)) ||
         description.toLowerCase().includes(img.description.toLowerCase().substring(0, 15)))
      );

      if (matchingImage?.imageData) {
        // Add actual AI-generated image
        const imgHeight = 80;
        if (yPos + imgHeight > pageHeight - 20) {
          doc.addPage();
          yPos = 20;
        }

        try {
          // Add a nice border/frame around the image
          doc.setFillColor(250, 252, 255);
          doc.setDrawColor(70, 130, 180);
          doc.setLineWidth(0.5);
          doc.roundedRect(margin - 2, yPos - 2, maxWidth + 4, imgHeight + 20, 3, 3, 'FD');
          
          // Add the image
          doc.addImage(matchingImage.imageData, 'PNG', margin + 5, yPos + 2, maxWidth - 10, imgHeight - 10);
          
          // Add caption below image
          doc.setFontSize(8);
          doc.setFont("helvetica", "italic");
          doc.setTextColor(100, 100, 100);
          const captionLines = doc.splitTextToSize(`Figure: ${sanitizedDescription}`, maxWidth - 20);
          doc.text(captionLines[0], margin + 5, yPos + imgHeight);
          
          yPos += imgHeight + 25;
        } catch (imgError) {
          console.error("Error adding image to PDF:", imgError);
          // Fall back to placeholder
          addPlaceholderBox(description);
        }
      } else {
        // Show placeholder if no image generated
        addPlaceholderBox(sanitizedDescription);
      }
    };

    const addPlaceholderBox = (description: string) => {
      const sanitizedDesc = sanitizeForPDF(description);
      const boxHeight = 50;
      if (yPos + boxHeight > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      
      // Create a professional visual element box
      doc.setFillColor(245, 250, 255);
      doc.setDrawColor(70, 130, 180);
      doc.setLineWidth(1);
      doc.roundedRect(margin, yPos, maxWidth, boxHeight, 4, 4, 'FD');
      
      // Add corner brackets to indicate "visual placeholder"
      doc.setDrawColor(70, 130, 180);
      doc.setLineWidth(2);
      // Top-left bracket
      doc.line(margin + 5, yPos + 5, margin + 5, yPos + 15);
      doc.line(margin + 5, yPos + 5, margin + 15, yPos + 5);
      // Top-right bracket
      doc.line(margin + maxWidth - 5, yPos + 5, margin + maxWidth - 5, yPos + 15);
      doc.line(margin + maxWidth - 5, yPos + 5, margin + maxWidth - 15, yPos + 5);
      // Bottom-left bracket
      doc.line(margin + 5, yPos + boxHeight - 5, margin + 5, yPos + boxHeight - 15);
      doc.line(margin + 5, yPos + boxHeight - 5, margin + 15, yPos + boxHeight - 5);
      // Bottom-right bracket
      doc.line(margin + maxWidth - 5, yPos + boxHeight - 5, margin + maxWidth - 5, yPos + boxHeight - 15);
      doc.line(margin + maxWidth - 5, yPos + boxHeight - 5, margin + maxWidth - 15, yPos + boxHeight - 5);
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(70, 130, 180);
      doc.text("[VISUAL ELEMENT]", margin + 20, yPos + 15);
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      const descLines = doc.splitTextToSize(sanitizedDesc, maxWidth - 30);
      doc.text(descLines.slice(0, 2), margin + 20, yPos + 28);
      
      yPos += boxHeight + 10;
    };

    const addActionBox = (title: string, items: string[]) => {
      const sanitizedTitle = sanitizeForPDF(title);
      const sanitizedItems = items.map(item => sanitizeForPDF(item));
      const lineHeight = 6;
      const boxHeight = 25 + sanitizedItems.length * lineHeight;
      if (yPos + boxHeight > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFillColor(255, 250, 240);
      doc.setDrawColor(255, 165, 0);
      doc.setLineWidth(1);
      doc.roundedRect(margin, yPos, maxWidth, boxHeight, 4, 4, 'FD');
      
      // Add checkmark indicator with text
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(200, 100, 0);
      doc.text(`[ACTION] ${sanitizedTitle}`, margin + 8, yPos + 12);
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60, 60, 60);
      sanitizedItems.forEach((item, i) => {
        // Use simple bullet character
        const itemLines = doc.splitTextToSize(`- ${item}`, maxWidth - 20);
        doc.text(itemLines[0], margin + 12, yPos + 22 + i * lineHeight);
      });
      
      yPos += boxHeight + 10;
    };

    // Cover Page
    doc.setFillColor(30, 40, 60);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    doc.setFontSize(32);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    const mainTitle = sanitizeForPDF(content.titles?.[0]?.main || product.title);
    const titleLines = doc.splitTextToSize(mainTitle, maxWidth);
    doc.text(titleLines, pageWidth / 2, pageHeight / 3, { align: 'center' });
    
    if (content.titles?.[0]?.subtitle) {
      doc.setFontSize(16);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(200, 200, 200);
      const subtitleLines = doc.splitTextToSize(sanitizeForPDF(content.titles[0].subtitle), maxWidth);
      doc.text(subtitleLines, pageWidth / 2, pageHeight / 3 + 30, { align: 'center' });
    }
    
    if (content.titles?.[0]?.transformation || content.transformation) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 200, 150);
      const transText = sanitizeForPDF(content.titles?.[0]?.transformation || content.transformation || '');
      const transLines = doc.splitTextToSize(transText, maxWidth - 40);
      doc.text(transLines, pageWidth / 2, pageHeight * 0.6, { align: 'center' });
    }
    
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text("Created with Monetization AI", pageWidth / 2, pageHeight - 30, { align: 'center' });
    
    // Start content
    doc.addPage();
    yPos = 20;

    // About This Book Section
    addText("About This Book", 18, "bold", [30, 30, 30]);
    addSpace(5);

    if (content.positioning) {
      addBox("Positioning", content.positioning);
    }

    const target = content.targetReader || content.targetStudent || content.targetUser;
    if (target) {
      addBox("Who This Is For", formatTargetAudience(target));
    }

    if (content.transformation) {
      addBox("Your Transformation", content.transformation, [240, 255, 240]);
    }

    addDivider();

    // Table of Contents
    addText("Table of Contents", 18, "bold", [30, 30, 30]);
    addSpace(5);

    if (content.chapters && content.chapters.length > 0) {
      content.chapters.forEach((chapter: any) => {
        addText(`Chapter ${chapter.number}: ${chapter.title}`, 12, "bold", [50, 50, 50]);
        if (chapter.description) {
          addText(chapter.description, 10, "normal", [100, 100, 100]);
        }
        addSpace(3);
      });
    }

    // Full Chapter Content
    if (content.chapters && content.chapters.length > 0) {
      content.chapters.forEach((chapter: any, i: number) => {
        doc.addPage();
        yPos = 20;
        
        // Chapter header
        doc.setFillColor(30, 40, 60);
        doc.rect(0, 0, pageWidth, 50, 'F');
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 200, 150);
        doc.text(`CHAPTER ${chapter.number}`, margin, 25);
        
        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        const chapterTitleLines = doc.splitTextToSize(chapter.title, maxWidth);
        doc.text(chapterTitleLines[0], margin, 40);
        
        yPos = 65;

        // Chapter content
        if (chapterContent[i]) {
          const chapterText = chapterContent[i];
          
          // Parse markdown-style content and render with formatting
          const lines = chapterText.split('\n');
          
          for (const line of lines) {
            const trimmedLine = line.trim();
            
            // Check for page break
            if (yPos > pageHeight - 40) {
              doc.addPage();
              yPos = 20;
            }
            
            // Handle visual placeholders
            if (trimmedLine.startsWith('[IMAGE:') || trimmedLine.startsWith('[DIAGRAM:') || trimmedLine.startsWith('[INFOGRAPHIC:') || 
                trimmedLine.startsWith('[FLOWCHART:')) {
              const description = trimmedLine.replace(/\[.*?:\s*/, '').replace(/\]$/, '');
              addVisualPlaceholder(description, i);
              continue;
            }
            
            // Handle headers
            if (trimmedLine.startsWith('## ')) {
              addSpace(8);
              addText(trimmedLine.replace('## ', ''), 14, "bold", [30, 30, 30]);
              addSpace(3);
              continue;
            }
            
            if (trimmedLine.startsWith('### ')) {
              addSpace(5);
              addText(trimmedLine.replace('### ', ''), 12, "bold", [50, 50, 50]);
              addSpace(2);
              continue;
            }
            
            // Handle "Your Turn" / "Action Step" sections
            if (trimmedLine.toLowerCase().includes('your turn:') || 
                trimmedLine.toLowerCase().includes('action step:') ||
                trimmedLine.toLowerCase().includes('exercise:')) {
              addSpace(5);
              const actionItems = [];
              let j = lines.indexOf(line) + 1;
              while (j < lines.length && lines[j].trim().startsWith('-')) {
                actionItems.push(lines[j].trim().replace(/^-\s*/, ''));
                j++;
              }
              if (actionItems.length > 0) {
                addActionBox(trimmedLine.replace(/[:#]/g, '').trim(), actionItems);
              } else {
                addText(trimmedLine, 11, "bold", [200, 100, 0]);
              }
              continue;
            }
            
            // Handle "Key Takeaways" section
            if (trimmedLine.toLowerCase().includes('key takeaways')) {
              addSpace(8);
              doc.setFillColor(240, 248, 240);
              const takeawayItems = [];
              let j = lines.indexOf(line) + 1;
              while (j < lines.length && lines[j].trim().startsWith('-')) {
                takeawayItems.push(lines[j].trim().replace(/^-\s*/, ''));
                j++;
              }
              if (takeawayItems.length > 0) {
                addActionBox("Key Takeaways", takeawayItems);
              } else {
                addText(trimmedLine, 12, "bold", [0, 100, 0]);
              }
              continue;
            }
            
            // Handle markdown tables (pipe-delimited rows)
            if (trimmedLine.includes('|') && trimmedLine.split('|').length >= 3) {
              addTableRow(trimmedLine, doc, margin, maxWidth);
              continue;
            }

            // Handle bullet points
            if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
              addText(`  - ${trimmedLine.substring(2)}`, 10, "normal", [60, 60, 60]);
              continue;
            }
            
            // Handle numbered lists
            if (/^\d+\.\s/.test(trimmedLine)) {
              addText(trimmedLine, 10, "normal", [60, 60, 60]);
              continue;
            }
            
            // Handle bold text
            if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
              addText(trimmedLine.replace(/\*\*/g, ''), 11, "bold", [40, 40, 40]);
              continue;
            }
            
            // Regular paragraphs
            if (trimmedLine) {
              addText(trimmedLine.replace(/\*\*/g, '').replace(/\*/g, ''), 10, "normal", [40, 40, 40]);
            } else {
              addSpace(3);
            }
          }
        } else {
          // No content generated yet - show placeholder
          addText("Content for this chapter has not been generated yet.", 11, "italic", [150, 150, 150]);
          addSpace(10);
          addText("Key points that will be covered:", 12, "bold", [80, 80, 80]);
          addSpace(3);
          
          if (chapter.keyPoints) {
            chapter.keyPoints.forEach((point: string) => {
              addText(`  - ${point}`, 10, "normal", [100, 100, 100]);
            });
          }
          
          if (chapter.suggestedVisuals) {
            addSpace(10);
            addText("Suggested visuals:", 11, "bold", [80, 80, 80]);
            chapter.suggestedVisuals.forEach((visual: string) => {
              addVisualPlaceholder(visual, i);
            });
          }
        }
      });
    }

    // Course Modules (for courses)
    if (content.modules && content.modules.length > 0) {
      doc.addPage();
      yPos = 20;
      addText("Course Outline", 18, "bold");
      addSpace(5);

      content.modules.forEach((module: any, moduleIdx: number) => {
        addText(`Module ${module.number}: ${module.title}`, 14, "bold");
        if (module.description) {
          addText(module.description, 11, "normal", [80, 80, 80]);
        }
        addSpace(3);
        
        if (module.lessons) {
          module.lessons.forEach((lesson: any, lessonIdx: number) => {
            addText(`  > ${lesson.title} (${lesson.duration})`, 10);
            if (lesson.description) {
              addText(`      ${lesson.description}`, 9, "normal", [100, 100, 100]);
            }
          });
        }
        if (module.homework) {
          addSpace(2);
          addBox("Homework", module.homework, [255, 250, 240]);
        }
        addSpace(5);
      });

      // Lesson Scripts Section
      const hasAnyScripts = Object.keys(lessonScripts).length > 0;
      if (hasAnyScripts) {
        doc.addPage();
        yPos = 20;
        addText("Lesson Scripts", 18, "bold");
        addSpace(5);

        content.modules.forEach((module: any, moduleIdx: number) => {
          if (module.lessons) {
            module.lessons.forEach((lesson: any, lessonIdx: number) => {
              const lessonKey = `${moduleIdx}-${lessonIdx}`;
              const script = lessonScripts[lessonKey];
              
              if (script) {
                // Module/Lesson header
                doc.addPage();
                yPos = 20;
                
                doc.setFillColor(30, 40, 60);
                doc.rect(0, 0, pageWidth, 50, 'F');
                
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(100, 200, 150);
                doc.text(`MODULE ${module.number} - LESSON ${lessonIdx + 1}`, margin, 20);
                
                doc.setFontSize(18);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(255, 255, 255);
                const lessonTitleLines = doc.splitTextToSize(lesson.title, maxWidth);
                doc.text(lessonTitleLines[0], margin, 38);
                
                yPos = 65;
                
                // Script content
                const lines = script.split('\n');
                for (const line of lines) {
                  const trimmedLine = line.trim();
                  
                  if (yPos > pageHeight - 40) {
                    doc.addPage();
                    yPos = 20;
                  }
                  
                  // Handle headers
                  if (trimmedLine.startsWith('## ')) {
                    addSpace(8);
                    addText(trimmedLine.replace('## ', ''), 14, "bold", [30, 30, 30]);
                    addSpace(3);
                    continue;
                  }
                  
                  if (trimmedLine.startsWith('### ')) {
                    addSpace(5);
                    addText(trimmedLine.replace('### ', ''), 12, "bold", [50, 50, 50]);
                    addSpace(2);
                    continue;
                  }
                  
                  // Handle bullet points
                  if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
                    addText(`  - ${trimmedLine.substring(2)}`, 10, "normal", [60, 60, 60]);
                    continue;
                  }
                  
                  // Regular paragraphs
                  if (trimmedLine) {
                    addText(trimmedLine.replace(/\*\*/g, '').replace(/\*/g, ''), 10, "normal", [40, 40, 40]);
                  } else {
                    addSpace(3);
                  }
                }
              }
            });
          }
        });
      }
    }

    // Asset Pack / Templates & Checklists
    if (content.assets && content.assets.length > 0) {
      doc.addPage();
      yPos = 20;
      addText(isToolkitType ? "Templates & Checklists" : "Assets Included", 18, "bold");
      addSpace(5);

      content.assets.forEach((category: any) => {
        addText(category.category, 14, "bold");
        if (category.categoryDescription) {
          addText(category.categoryDescription, 10, "normal", [80, 80, 80]);
        }
        addSpace(3);
        
        if (category.items) {
          category.items.forEach((item: any) => {
            // Item header with type badge - use ASCII-safe labels (no emoji)
            const typeLabel = item.type === "checklist" ? "[CHECKLIST]" : 
                             item.type === "template" ? "[TEMPLATE]" :
                             item.type === "worksheet" ? "[WORKSHEET]" :
                             item.type === "framework" ? "[FRAMEWORK]" : "[+]";
            
            addText(`${typeLabel}  ${item.name}`, 11, "bold", [50, 50, 50]);
            addText(`    ${item.description}`, 9, "normal", [80, 80, 80]);
            
            // Render actual content for toolkits
            if (isToolkitType && item.fullContent) {
              addSpace(3);
              
              // Draw a light box around the content
              const startY = yPos;
              doc.setDrawColor(180, 200, 180);
              doc.setLineWidth(0.5);
              
              const contentLines = item.fullContent.split('\n');
              for (const line of contentLines) {
                const trimmed = line.trim();
                if (!trimmed) { addSpace(2); continue; }
                
                // Handle image placeholders in toolkit items
                if (trimmed.startsWith('[IMAGE:')) {
                  const description = trimmed.replace(/\[IMAGE:\s*/, '').replace(/\]$/, '');
                  addVisualPlaceholder(description, -1);
                }
                // Render checkbox items - use ASCII square bracket
                else if (trimmed.startsWith('[ ]') || trimmed.startsWith('- [ ]') || trimmed.startsWith('☐') || trimmed.match(/^-\s*\[/)) {
                  const cleanText = trimmed.replace(/^(-\s*)?\[[\s]*\]\s*|^☐\s*/, '');
                  addText(`  [ ]  ${cleanText}`, 9, "normal", [40, 40, 40]);
                }
                // Render placeholder items (but not IMAGE tags)
                else if (trimmed.includes('[') && trimmed.includes(']') && !trimmed.startsWith('[IMAGE:')) {
                  addText(`    ${trimmed}`, 9, "italic", [60, 60, 60]);
                }
                // Detect table-like content (pipes)
                else if (trimmed.includes('|') && trimmed.split('|').length >= 3) {
                  addTableRow(trimmed, doc, margin, maxWidth);
                }
                // Headers
                else if (trimmed.startsWith('#')) {
                  addSpace(3);
                  addText(trimmed.replace(/^#+\s*/, ''), 10, "bold", [30, 30, 30]);
                }
                // Bold markers
                else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                  addText(`    ${trimmed.replace(/\*\*/g, '')}`, 9, "bold", [40, 40, 40]);
                }
                // Regular lines
                else {
                  addText(`    ${trimmed.replace(/\*\*/g, '')}`, 9, "normal", [50, 50, 50]);
                }
              }
              addSpace(3);
            }
            
            if (item.problemSolved) {
              addText(`    Problem solved: ${item.problemSolved}`, 9, "italic", [100, 100, 100]);
            }
            if (item.expectedOutcome) {
              addText(`    Expected outcome: ${item.expectedOutcome}`, 9, "normal", [0, 120, 60]);
            }
            addSpace(4);
          });
        }
        addSpace(5);
      });
    }

    // Bonuses Section
    if (content.bonuses && content.bonuses.length > 0) {
      doc.addPage();
      yPos = 20;
      addText("Bonus Content", 18, "bold");
      addSpace(5);
      
      content.bonuses.forEach((bonus: any) => {
        addText(`[BONUS] ${formatListItem(bonus)}`, 11);
        addSpace(2);
      });
    }

    // Final page
    doc.addPage();
    yPos = pageHeight / 3;
    
    doc.setFillColor(30, 40, 60);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("Thank You!", pageWidth / 2, yPos, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(200, 200, 200);
    doc.text("We hope this content helps you on your journey.", pageWidth / 2, yPos + 20, { align: 'center' });
    
    if (content.callToActions && content.callToActions.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(100, 200, 150);
      doc.text("What's Next?", pageWidth / 2, yPos + 50, { align: 'center' });
      
      doc.setFontSize(10);
      doc.setTextColor(180, 180, 180);
      content.callToActions.forEach((cta: any, i) => {
        doc.text(`→ ${formatListItem(cta)}`, pageWidth / 2, yPos + 65 + i * 12, { align: 'center' });
      });
    }

    // Save
    const fileName = `${mainTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_complete.pdf`;
    doc.save(fileName);
    toast.success("Complete PDF exported successfully!");
    } catch (err) {
      console.error("PDF build error:", err);
      toast.error("Failed to build PDF. Please try again.");
    }
  };

  const handleExportMarkdown = () => {
    if (!product || !content) return;

    let markdown = "";
    const mainTitle = content.titles?.[0]?.main || product.title;

    markdown += `# ${mainTitle}\n\n`;
    if (content.titles?.[0]?.subtitle) {
      markdown += `*${content.titles[0].subtitle}*\n\n`;
    }
    if (content.titles?.[0]?.transformation || content.transformation) {
      markdown += `> **Transformation:** ${content.titles?.[0]?.transformation || content.transformation}\n\n`;
    }

    markdown += `---\n\n`;
    markdown += `## About This Book\n\n`;

    if (content.positioning) {
      markdown += `### Positioning\n\n${content.positioning}\n\n`;
    }

    const target = content.targetReader || content.targetStudent || content.targetUser;
    if (target) {
      markdown += `### Who This Is For\n\n${formatTargetAudience(target)}\n\n`;
    }

    if (content.transformation) {
      markdown += `### Your Transformation\n\n${content.transformation}\n\n`;
    }

    markdown += `---\n\n`;

    // Chapters
    if (content.chapters && content.chapters.length > 0) {
      markdown += `## Table of Contents\n\n`;
      content.chapters.forEach((chapter: any) => {
        markdown += `- **Chapter ${chapter.number}:** ${chapter.title}\n`;
      });
      markdown += `\n---\n\n`;

      content.chapters.forEach((chapter: any, i: number) => {
        markdown += `# Chapter ${chapter.number}: ${chapter.title}\n\n`;
        
        if (chapterContent[i]) {
          markdown += `${chapterContent[i]}\n\n`;
        } else {
          if (chapter.description) {
            markdown += `*${chapter.description}*\n\n`;
          }
          if (chapter.keyPoints) {
            markdown += `**Key Points:**\n`;
            chapter.keyPoints.forEach((point: string) => {
              markdown += `- ${point}\n`;
            });
            markdown += `\n`;
          }
          if (chapter.suggestedVisuals) {
            markdown += `**Suggested Visuals:**\n`;
            chapter.suggestedVisuals.forEach((visual: string) => {
              markdown += `- ${visual}\n`;
            });
            markdown += `\n`;
          }
        }
        markdown += `---\n\n`;
      });
    }

    // Modules
    if (content.modules && content.modules.length > 0) {
      markdown += `## Course Outline\n\n`;
      content.modules.forEach((module: any) => {
        markdown += `### Module ${module.number}: ${module.title}\n\n`;
        if (module.description) {
          markdown += `${module.description}\n\n`;
        }
        if (module.lessons) {
          markdown += `**Lessons:**\n`;
          module.lessons.forEach((lesson: any) => {
            markdown += `- 📹 ${lesson.title} (${lesson.duration})\n`;
            if (lesson.description) {
              markdown += `  - ${lesson.description}\n`;
            }
          });
          markdown += `\n`;
        }
        if (module.homework) {
          markdown += `**Homework:** ${module.homework}\n\n`;
        }
      });

      // Lesson Scripts
      const hasAnyScripts = Object.keys(lessonScripts).length > 0;
      if (hasAnyScripts) {
        markdown += `---\n\n`;
        markdown += `## Lesson Scripts\n\n`;
        
        content.modules.forEach((module: any, moduleIdx: number) => {
          if (module.lessons) {
            module.lessons.forEach((lesson: any, lessonIdx: number) => {
              const lessonKey = `${moduleIdx}-${lessonIdx}`;
              const script = lessonScripts[lessonKey];
              
              if (script) {
                markdown += `### Module ${module.number} - Lesson ${lessonIdx + 1}: ${lesson.title}\n\n`;
                markdown += `${script}\n\n`;
                markdown += `---\n\n`;
              }
            });
          }
        });
      }
    }

    // Assets / Templates & Checklists
    if (content.assets && content.assets.length > 0) {
      markdown += `## ${isToolkit ? 'Templates & Checklists' : 'Assets Included'}\n\n`;
      content.assets.forEach((category: any) => {
        markdown += `### ${category.category}\n\n`;
        if (category.categoryDescription) {
          markdown += `*${category.categoryDescription}*\n\n`;
        }
        if (category.items) {
          category.items.forEach((item: any) => {
            markdown += `- **${item.name}** (${item.type || item.format}): ${item.description}\n`;
            if (isToolkit && item.fullContent) {
              markdown += `\n${item.fullContent}\n\n`;
            }
            if (item.howToUse) {
              markdown += `  - *How to use:* ${item.howToUse}\n`;
            }
            if (item.expectedOutcome) {
              markdown += `  - ✓ *Expected outcome:* ${item.expectedOutcome}\n`;
            }
          });
          markdown += `\n`;
        }
      });
    }

    // Bonuses
    if (content.bonuses && content.bonuses.length > 0) {
      markdown += `## Bonus Content\n\n`;
      content.bonuses.forEach((bonus: any) => {
        markdown += `- 🎁 ${formatListItem(bonus)}\n`;
      });
      markdown += `\n`;
    }

    // Call to Actions
    if (content.callToActions && content.callToActions.length > 0) {
      markdown += `## What's Next?\n\n`;
      content.callToActions.forEach((cta: any) => {
        markdown += `→ ${formatListItem(cta)}\n`;
      });
    }

    // Download as file
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${mainTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_complete.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Complete Markdown exported successfully!");
  };

  const handleContinue = async () => {
    await supabase
      .from("monetization_projects")
      .update({ last_step: "pricing" })
      .eq("id", projectId);

    navigate(`/app/monetization-ai/project/${projectId}/pricing/${productId}`);
  };

  const handleCourseDeliveryExport = (type: "script-first" | "slides-voiceover" | "course-in-a-box") => {
    if (!content?.modules || !product) return;

    const courseTitle = content.titles?.[0]?.main || product.title;
    const now = new Date().toISOString().split('T')[0];

    if (type === "script-first") {
      // Export lesson scripts with visual direction notes
      let markdown = `# ${courseTitle}\n## Script-First Video Course Package\n\n`;
      markdown += `**Generated:** ${now}\n\n---\n\n`;
      markdown += `## 📹 How to Use These Scripts\n\n`;
      markdown += `1. **Record the voiceover** - Use these scripts exactly as written, or adapt them to your style\n`;
      markdown += `2. **Add visuals** - Stock footage, B-roll, screen recordings, or text on screen\n`;
      markdown += `3. **Edit in any tool** - CapCut, Canva, Premiere Pro, or whatever you prefer\n`;
      markdown += `4. **Export and upload** - Publish to your course platform\n\n`;
      markdown += `> 💡 **No face required!** These scripts work perfectly for faceless videos.\n\n---\n\n`;

      content.modules.forEach((module: any, mi: number) => {
        markdown += `# Module ${module.number || mi + 1}: ${module.title}\n\n`;
        markdown += `${module.description || ""}\n\n`;

        module.lessons?.forEach((lesson: any, li: number) => {
          const key = `${mi}-${li}`;
          const script = lessonScripts[key];
          
          markdown += `## Lesson ${li + 1}: ${lesson.title}\n`;
          markdown += `**Duration:** ${lesson.duration || "5-10 minutes"}\n\n`;
          
          if (lesson.description) {
            markdown += `**Overview:** ${lesson.description}\n\n`;
          }
          
          markdown += `### 🎙️ Video Script\n\n`;
          markdown += script ? `${script}\n\n` : `*Script not yet generated*\n\n`;
          
          markdown += `### 🎬 Visual Direction Notes\n\n`;
          markdown += `- Consider using B-roll footage related to the topic\n`;
          markdown += `- Add text overlays for key points\n`;
          markdown += `- Include relevant graphics or animations\n\n`;
          markdown += `---\n\n`;
        });
      });

      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${courseTitle.replace(/[^a-z0-9]/gi, "-")}-Scripts-Package.md`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Scripts package downloaded!");
    } 
    else if (type === "slides-voiceover") {
      // Export slide outlines with voiceover scripts
      let markdown = `# ${courseTitle}\n## Slides + Voiceover Package\n\n`;
      markdown += `**Generated:** ${now}\n\n---\n\n`;
      markdown += `## 🎯 How to Use This Package\n\n`;
      markdown += `1. **Create slides** - Use the slide titles and bullet points in Canva, PowerPoint, or Google Slides\n`;
      markdown += `2. **Record voiceover** - Read the voiceover script while showing each slide (or use AI voice)\n`;
      markdown += `3. **Export as video** - Save each lesson as an MP4 video\n`;
      markdown += `4. **Upload to platform** - Add to your course platform of choice\n\n`;
      markdown += `> 💡 **This is the easiest option!** Perfect for complete beginners.\n\n---\n\n`;

      content.modules.forEach((module: any, mi: number) => {
        markdown += `# Module ${module.number || mi + 1}: ${module.title}\n\n`;

        module.lessons?.forEach((lesson: any, li: number) => {
          const key = `${mi}-${li}`;
          const script = lessonScripts[key];
          
          markdown += `## Lesson ${li + 1}: ${lesson.title}\n`;
          markdown += `**Duration:** ${lesson.duration || "5-10 minutes"}\n\n`;
          
          // Generate slide suggestions from the script
          markdown += `### 📊 Slide Outline\n\n`;
          markdown += `**Slide 1: Title Slide**\n`;
          markdown += `- Title: "${lesson.title}"\n`;
          markdown += `- Subtitle: Module ${module.number || mi + 1}\n\n`;
          
          if (script) {
            // Break script into logical sections for slides
            const paragraphs = script.split('\n\n').filter((p: string) => p.trim().length > 30);
            paragraphs.slice(0, 6).forEach((para: string, pi: number) => {
              const firstSentence = para.split('.')[0] + '.';
              markdown += `**Slide ${pi + 2}:**\n`;
              markdown += `- Key Point: ${firstSentence.substring(0, 100)}...\n`;
              markdown += `- Bullet points: Extract 2-3 key ideas from this section\n\n`;
            });
          } else {
            markdown += `*Generate the lesson script first to get detailed slide content*\n\n`;
          }
          
          markdown += `### 🎙️ Voiceover Script\n\n`;
          markdown += script ? `${script}\n\n` : `*Script not yet generated*\n\n`;
          markdown += `---\n\n`;
        });
      });

      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${courseTitle.replace(/[^a-z0-9]/gi, "-")}-Slides-Voiceover-Package.md`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Slides + Voiceover package downloaded!");
    } 
    else if (type === "course-in-a-box") {
      // Export complete course package
      let markdown = `# ${courseTitle}\n## Complete Course-in-a-Box Package\n\n`;
      markdown += `**Generated:** ${now}\n\n---\n\n`;
      markdown += `## 📦 What's Included in This Package\n\n`;
      markdown += `✅ All video scripts for every lesson\n`;
      markdown += `✅ Slide outlines for presentations\n`;
      markdown += `✅ Workbook activities and exercises\n`;
      markdown += `✅ Lesson checklists for students\n`;
      markdown += `✅ Lesson summaries\n`;
      markdown += `✅ Sales page copy\n\n`;
      markdown += `> 🎉 **This product is ready to sell!** Videos can be added later if you want.\n\n---\n\n`;

      // Course Overview
      markdown += `# Part 1: Course Overview\n\n`;
      markdown += `## Course Description\n\n`;
      markdown += content.positioning ? `${content.positioning}\n\n` : `${product.title} - A comprehensive course.\n\n`;
      
      if (content.transformation) {
        markdown += `## Transformation Promise\n\n${content.transformation}\n\n`;
      }
      
      if (content.targetStudent) {
        markdown += `## Target Student\n\n${formatTargetAudience(content.targetStudent)}\n\n`;
      }

      markdown += `---\n\n# Part 2: Complete Video Scripts\n\n`;
      
      content.modules.forEach((module: any, mi: number) => {
        markdown += `## Module ${module.number || mi + 1}: ${module.title}\n\n`;
        markdown += `${module.description || ""}\n\n`;

        module.lessons?.forEach((lesson: any, li: number) => {
          const key = `${mi}-${li}`;
          const script = lessonScripts[key];
          
          markdown += `### Lesson ${li + 1}: ${lesson.title}\n`;
          markdown += `**Duration:** ${lesson.duration || "5-10 minutes"}\n\n`;
          markdown += script ? `${script}\n\n` : `*Script not yet generated*\n\n`;
        });
        
        if (module.homework) {
          markdown += `**Module Homework:** ${module.homework}\n\n`;
        }
        markdown += `---\n\n`;
      });

      // Workbook Section
      markdown += `# Part 3: Student Workbook\n\n`;
      content.modules.forEach((module: any, mi: number) => {
        markdown += `## Module ${module.number || mi + 1} Exercises\n\n`;
        markdown += `### Reflection Questions\n\n`;
        markdown += `1. What was your biggest takeaway from this module?\n`;
        markdown += `2. How can you apply what you learned today?\n`;
        markdown += `3. What questions do you still have?\n\n`;
        
        markdown += `### Action Items\n\n`;
        markdown += `- [ ] Complete the main exercise from this module\n`;
        markdown += `- [ ] Take notes on key concepts\n`;
        markdown += `- [ ] Share your progress in the community\n\n`;
      });

      // Checklists
      markdown += `---\n\n# Part 4: Student Checklists\n\n`;
      content.modules.forEach((module: any, mi: number) => {
        markdown += `## Module ${module.number || mi + 1} Checklist\n\n`;
        module.lessons?.forEach((lesson: any, li: number) => {
          markdown += `- [ ] Complete Lesson ${li + 1}: ${lesson.title}\n`;
        });
        if (module.homework) {
          markdown += `- [ ] Complete homework assignment\n`;
        }
        markdown += `\n`;
      });

      // Lesson Summaries
      markdown += `---\n\n# Part 5: Lesson Summaries\n\n`;
      content.modules.forEach((module: any, mi: number) => {
        markdown += `## Module ${module.number || mi + 1}: ${module.title}\n\n`;
        module.lessons?.forEach((lesson: any, li: number) => {
          markdown += `**${lesson.title}:** ${lesson.description || "Key concepts and actionable insights."}\n\n`;
        });
      });

      // Sales Page Copy
      markdown += `---\n\n# Part 6: Sales Page Copy\n\n`;
      markdown += `## Headline\n\n`;
      markdown += `**${courseTitle}**\n\n`;
      
      if (content.titles?.[0]?.subtitle) {
        markdown += `*${content.titles[0].subtitle}*\n\n`;
      }
      
      markdown += `## What You'll Learn\n\n`;
      content.modules.forEach((module: any, mi: number) => {
        markdown += `✓ ${module.title}\n`;
      });
      markdown += `\n`;
      
      if (content.bonuses && content.bonuses.length > 0) {
        markdown += `## Bonuses Included\n\n`;
        content.bonuses.forEach((bonus: any) => {
          markdown += `🎁 ${formatListItem(bonus)}\n`;
        });
        markdown += `\n`;
      }
      
      if (content.callToActions && content.callToActions.length > 0) {
        markdown += `## Call to Action\n\n`;
        markdown += `${formatListItem(content.callToActions[0])}\n\n`;
      }

      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${courseTitle.replace(/[^a-z0-9]/gi, "-")}-Complete-Course-Package.md`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Complete Course-in-a-Box package downloaded!");
    }
  };

  const isAllContentGenerated = () => {
    if (!content?.chapters) return false;
    return content.chapters.every((_, i) => chapterContent[i]);
  };

  const getGeneratedCount = () => {
    if (!content?.chapters) return 0;
    return Object.keys(chapterContent).length;
  };

  if (loading || generating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-green-500" />
        <p className="text-muted-foreground">
          {generating ? "Building your product structure..." : "Loading..."}
        </p>
      </div>
    );
  }

  const isEbook = product?.product_type === "ebook";
  const isCourse = product?.product_type === "video_course" || product?.product_type === "mini_course";
  const isAssetPack = product?.product_type === "asset_pack";
  const isToolkit = product?.product_type === "toolkit";
  const isAssetBased = ["notion_template", "swipe_file", "newsletter_pack", "challenge_kit", "resource_bundle", "content_calendar", "workbook", "coaching_scripts"].includes(product?.product_type || "");

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(`/app/monetization-ai/project/${projectId}/products`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold">{product?.title}</h1>
            <p className="text-muted-foreground">Building your product structure</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ml-14">
          <MonetizationStepper currentStep="building" />
        </div>

        {/* Title Options */}
        {content?.titles && (
          <div className="glass-card p-6 border-green-500/20 mb-6">
            <h3 className="font-display text-lg font-semibold mb-4">Title Options</h3>
            <div className="grid gap-3">
              {content.titles.map((title, i) => (
                <div key={i} className="p-4 rounded-lg bg-muted/50 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-foreground">{title.main}</p>
                    {title.subtitle && <p className="text-sm text-muted-foreground">{title.subtitle}</p>}
                    {title.transformation && <p className="text-sm text-green-400">{title.transformation}</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleCopyContent(title.main)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overview */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {content?.positioning && (
            <div className="glass-card p-4 border-green-500/20">
              <p className="text-sm text-muted-foreground mb-1">Positioning</p>
              <p className="font-medium">{content.positioning}</p>
            </div>
          )}
          {(content?.targetReader || content?.targetStudent || content?.targetUser) && (
            <div className="glass-card p-4 border-green-500/20">
              <p className="text-sm text-muted-foreground mb-1">Target Audience</p>
              <p className="font-medium">{formatTargetAudience(content.targetReader || content.targetStudent || content.targetUser)}</p>
            </div>
          )}
          {content?.transformation && (
            <div className="glass-card p-4 border-green-500/20">
              <p className="text-sm text-muted-foreground mb-1">Transformation</p>
              <p className="font-medium">{content.transformation}</p>
            </div>
          )}
          {(content?.totalWordCount || content?.totalLessons || content?.totalAssets) && (
            <div className="glass-card p-4 border-green-500/20">
              <p className="text-sm text-muted-foreground mb-1">Size</p>
              <p className="font-medium">
                {isEbook && `${content.totalWordCount?.toLocaleString()} words (~${content.estimatedPages} pages)`}
                {isCourse && `${content.totalLessons} lessons (${content.totalDuration})`}
                {(isAssetPack || isToolkit || isAssetBased) && `${content.totalAssets} ${isToolkit ? 'templates & checklists' : 'items'}`}
              </p>
            </div>
          )}
        </div>

        {/* Generate All Button for eBooks */}
        {isEbook && content?.chapters && content.chapters.length > 0 && (
          <div className="glass-card p-6 border-green-500/20 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-display text-lg font-semibold mb-1">Generate Complete eBook</h3>
                <p className="text-sm text-muted-foreground">
                  {isAllContentGenerated() 
                    ? "All chapters generated! Ready to generate images." 
                    : `${getGeneratedCount()} of ${content.chapters.length} chapters generated`}
                </p>
              </div>
              <Button
                onClick={generateAllContent}
                disabled={generatingAll || isAllContentGenerated()}
                variant={isAllContentGenerated() ? "outline" : "hero"}
              >
                {generatingAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : isAllContentGenerated() ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    All Content Ready
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate All Chapters
                  </>
                )}
              </Button>
            </div>
            {generatingAll && (
              <div className="mt-4">
                <Progress value={generationProgress} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  Generating chapter content... {Math.round(generationProgress)}% complete
                </p>
              </div>
            )}
          </div>
        )}

        {/* Generate Images Section for eBooks */}
        {isEbook && content?.chapters && isAllContentGenerated() && (
          <div className="glass-card p-6 border-blue-500/20 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-400" />
                  Generate AI Diagrams & Images
                </h3>
                <p className="text-sm text-muted-foreground">
                  {generatedImages.length > 0 
                    ? `${generatedImages.length} images generated and ready for PDF` 
                    : "Generate professional diagrams and visuals to include in your PDF"}
                </p>
              </div>
              <Button
                onClick={generateAllImages}
                disabled={generatingImages}
                variant={generatedImages.length > 0 ? "outline" : "default"}
                className={generatedImages.length === 0 ? "bg-blue-600 hover:bg-blue-700" : ""}
              >
                {generatingImages ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Images...
                  </>
                ) : generatedImages.length > 0 ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate Images
                  </>
                ) : (
                  <>
                    <Image className="w-4 h-4 mr-2" />
                    Generate Images
                  </>
                )}
              </Button>
            </div>
            {generatingImages && (
              <div className="mt-4">
                <Progress value={imageProgress} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  Generating AI images... {Math.round(imageProgress)}% complete
                </p>
              </div>
            )}
            {generatedImages.length > 0 && !generatingImages && (
              <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <p className="text-sm text-blue-300">
                  {generatedImages.length} diagrams ready! Export your PDF to see the images embedded.
                </p>
              </div>
            )}
          </div>
        )}

        {/* eBook Chapters */}
        {isEbook && content?.chapters && (
          <div className="glass-card p-6 border-green-500/20 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">Chapters</h3>
              <span className="text-sm text-muted-foreground">{content.chapters.length} chapters</span>
            </div>
            <div className="space-y-2">
              {content.chapters.map((chapter, i) => (
                <div key={i} className="border border-border/50 rounded-lg overflow-hidden">
                  <div
                    onClick={() => setExpandedChapter(expandedChapter === i ? null : i)}
                    className="p-4 cursor-pointer flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 font-semibold">
                        {chapter.number}
                      </span>
                      <div>
                        <p className="font-medium">{chapter.title}</p>
                        <p className="text-sm text-muted-foreground">{chapter.wordCount} words</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {chapterContent[i] && <Check className="w-4 h-4 text-green-500" />}
                      {expandedChapter === i ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </div>
                  </div>
                  
                  {expandedChapter === i && (
                    <div className="p-4 border-t border-border/50 bg-muted/30">
                      <p className="text-sm text-muted-foreground mb-3">{chapter.description}</p>
                      
                      <div className="mb-4">
                        <p className="text-sm font-medium mb-2">Key Points:</p>
                        <ul className="list-disc list-inside text-sm text-muted-foreground">
                          {chapter.keyPoints?.map((point: string, j: number) => (
                            <li key={j}>{point}</li>
                          ))}
                        </ul>
                      </div>

                      {chapter.suggestedVisuals && (
                        <div className="mb-4">
                          <p className="text-sm font-medium mb-2">Suggested Visuals:</p>
                          <ul className="list-disc list-inside text-sm text-muted-foreground">
                            {chapter.suggestedVisuals.map((visual: string, j: number) => (
                              <li key={j}>{visual}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {chapterContent[i] ? (
                        <div>
                          {editingChapter === i ? (
                            <div className="space-y-2">
                              <Textarea
                                value={chapterContent[i]}
                                onChange={(e) => setChapterContent((prev) => ({ ...prev, [i]: e.target.value }))}
                                className="min-h-[300px] font-mono text-sm"
                              />
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleSaveChapter(i)}>
                                  <Save className="w-4 h-4 mr-2" /> Save
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingChapter(null)}>
                                  <X className="w-4 h-4 mr-2" /> Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="bg-background rounded-lg p-4 max-h-[400px] overflow-y-auto mb-2">
                                <pre className="text-sm whitespace-pre-wrap font-sans">{chapterContent[i]}</pre>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditingChapter(i)}>
                                  <Edit2 className="w-4 h-4 mr-2" /> Edit
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleCopyContent(chapterContent[i])}>
                                  <Copy className="w-4 h-4 mr-2" /> Copy
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => generateChapterContent(i)}>
                                  <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Button
                          onClick={() => generateChapterContent(i)}
                          disabled={generatingChapter !== null || generatingAll}
                        >
                          {generatingChapter === i ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <BookOpen className="w-4 h-4 mr-2" />
                              Generate Chapter Content
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Course Script Tone Selection */}
        {isCourse && content?.modules && (
          <div className="glass-card p-6 border-green-500/20 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Mic className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold">Script Tone & Style</h3>
                <p className="text-sm text-muted-foreground">How should your lesson scripts be written?</p>
              </div>
            </div>
            
            <RadioGroup
              value={scriptTone}
              onValueChange={saveToneSelection}
              className="grid md:grid-cols-2 gap-3"
            >
              {[
                { value: "conversational", label: "Conversational", desc: "Friendly, casual, like talking to a friend" },
                { value: "professional", label: "Professional", desc: "Polished, authoritative, industry expert" },
                { value: "energetic", label: "Energetic", desc: "Enthusiastic, motivational, high-energy" },
                { value: "calm", label: "Calm & Thoughtful", desc: "Measured, reflective, educational" },
                { value: "storytelling", label: "Storytelling", desc: "Narrative-driven, engaging with examples" },
                { value: "direct", label: "Direct & Concise", desc: "Straight to the point, no fluff" },
                { value: "custom", label: "Custom Style", desc: "Describe your own unique tone" },
              ].map((tone) => (
                <div key={tone.value} className="relative">
                  <RadioGroupItem
                    value={tone.value}
                    id={`tone-${tone.value}`}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={`tone-${tone.value}`}
                    className="flex flex-col p-4 rounded-lg border border-border/50 cursor-pointer hover:border-green-500/50 peer-data-[state=checked]:border-green-500 peer-data-[state=checked]:bg-green-500/10 transition-all"
                  >
                    <span className="font-medium">{tone.label}</span>
                    <span className="text-sm text-muted-foreground">{tone.desc}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {scriptTone === "custom" && (
              <div className="mt-4">
                <Label htmlFor="custom-tone-text" className="text-sm font-medium mb-2 block">
                  Describe your script style
                </Label>
                <Textarea
                  id="custom-tone-text"
                  value={customToneText}
                  onChange={(e) => setCustomToneText(e.target.value)}
                  onBlur={(e) => saveCustomToneText(e.target.value)}
                  placeholder="e.g., Warm and encouraging like a mentor, using simple analogies and real-world examples. Include moments of humor but stay focused on practical takeaways..."
                  className="min-h-[100px] border-green-500/30 focus:border-green-500"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Be specific about the voice, pacing, and style you want for your lesson scripts.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Course Script Generation */}
        {isCourse && content?.modules && scriptTone && (
          <div className="glass-card p-6 border-green-500/20 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-display text-lg font-semibold mb-1">Generate Lesson Scripts</h3>
                <p className="text-sm text-muted-foreground">
                  {getGeneratedLessonCount() === getTotalLessonCount()
                    ? "All lesson scripts generated!"
                    : `${getGeneratedLessonCount()} of ${getTotalLessonCount()} scripts generated`}
                </p>
              </div>
              <Button
                onClick={generateAllLessonScripts}
                disabled={generatingAllLessons || getGeneratedLessonCount() === getTotalLessonCount()}
                variant={getGeneratedLessonCount() === getTotalLessonCount() ? "outline" : "hero"}
              >
                {generatingAllLessons ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : getGeneratedLessonCount() === getTotalLessonCount() ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    All Scripts Ready
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate All Scripts
                  </>
                )}
              </Button>
            </div>
            {generatingAllLessons && (
              <div className="mt-4">
                <Progress value={lessonProgress} className="h-2" />
                <p className="text-xs text-muted-foreground mt-2">
                  Generating lesson scripts... {Math.round(lessonProgress)}% complete
                </p>
              </div>
            )}
          </div>
        )}

        {/* Course Modules */}
        {isCourse && content?.modules && (
          <div className="glass-card p-6 border-green-500/20 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">Modules & Lessons</h3>
              <span className="text-sm text-muted-foreground">{content.modules.length} modules</span>
            </div>
            <div className="space-y-3">
              {content.modules.map((module, i) => (
                <div key={i} className="border border-border/50 rounded-lg overflow-hidden">
                  <div
                    onClick={() => setExpandedModule(expandedModule === i ? null : i)}
                    className="p-4 cursor-pointer flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <span className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center text-green-400 font-semibold shrink-0">
                        {module.number}
                      </span>
                      <div>
                        <p className="font-semibold">{module.title}</p>
                        <p className="text-sm text-muted-foreground">{module.lessons?.length || 0} lessons</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {module.lessons?.every((_: any, j: number) => lessonScripts[`${i}-${j}`]) && (
                        <Check className="w-4 h-4 text-green-500" />
                      )}
                      {expandedModule === i ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </div>
                  </div>
                  
                  {expandedModule === i && (
                    <div className="border-t border-border/50 bg-muted/30 p-4">
                      <p className="text-sm text-muted-foreground mb-4">{module.description}</p>
                      
                      <div className="space-y-3">
                        {module.lessons?.map((lesson: any, j: number) => {
                          const lessonKey = `${i}-${j}`;
                          const hasScript = !!lessonScripts[lessonKey];
                          
                          return (
                            <div key={j} className="border border-border/30 rounded-lg p-3 bg-background/50">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Video className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-medium text-sm">{lesson.title}</span>
                                  {hasScript && <Check className="w-4 h-4 text-green-500" />}
                                </div>
                                <span className="text-xs text-muted-foreground">{lesson.duration}</span>
                              </div>
                              
                              {lesson.description && (
                                <p className="text-xs text-muted-foreground mb-3">{lesson.description}</p>
                              )}
                              
                              {hasScript ? (
                                <div>
                                  <div className="bg-muted rounded-lg p-3 max-h-[300px] overflow-y-auto mb-2">
                                    <pre className="text-sm whitespace-pre-wrap font-sans">{lessonScripts[lessonKey]}</pre>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => handleCopyContent(lessonScripts[lessonKey])}>
                                      <Copy className="w-3 h-3 mr-1" /> Copy
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      onClick={() => generateLessonScript(i, j, lesson, module)}
                                      disabled={generatingLesson !== null}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => generateLessonScript(i, j, lesson, module)}
                                  disabled={generatingLesson !== null || !scriptTone || generatingAllLessons}
                                >
                                  {generatingLesson === lessonKey ? (
                                    <>
                                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                      Generating...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="w-3 h-3 mr-1" />
                                      Generate Script
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {module.homework && (
                        <div className="mt-4 p-3 rounded-lg bg-primary/10 text-sm">
                          <span className="font-medium">Homework:</span> {module.homework}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Course Hosting Guidance Panel */}
        {isCourse && (
          <CourseHostingGuidance />
        )}

        {/* Asset Pack / New Product Type Categories */}
        {(isAssetPack || isToolkit || isAssetBased) && content?.assets && (
          <div className="glass-card p-6 border-green-500/20 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">{isToolkit ? 'Templates & Checklists' : 'Product Contents'}</h3>
            </div>
            <div className="space-y-4">
              {content.assets.map((category, i) => (
                <div key={i} className="border border-border/50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{category.category}</h4>
                  <div className="grid md:grid-cols-2 gap-2">
                    {category.items?.map((item: any, j: number) => (
                      <div key={j} className="p-3 rounded bg-muted/50">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                            {item.type || item.format}
                          </span>
                          <p className="font-medium text-sm">{item.name}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                        {isToolkit && item.fullContent && (
                          <div className="mt-2 p-2 rounded bg-background/50 border border-border/30">
                            <pre className="text-xs whitespace-pre-wrap font-sans text-muted-foreground max-h-[200px] overflow-y-auto">{item.fullContent}</pre>
                          </div>
                        )}
                        {item.expectedOutcome && (
                          <p className="text-xs text-green-400 mt-1">✓ {item.expectedOutcome}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bonuses & CTAs */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {content?.bonuses && content.bonuses.length > 0 && (
            <div className="glass-card p-4 border-green-500/20">
              <p className="text-sm font-medium mb-2">Bonus Ideas</p>
              <ul className="space-y-1">
                {content.bonuses.map((bonus, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                    <Check className="w-3 h-3 text-green-500" />
                    {formatListItem(bonus)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {content?.callToActions && content.callToActions.length > 0 && (
            <div className="glass-card p-4 border-green-500/20">
              <p className="text-sm font-medium mb-2">Call-to-Actions</p>
              <ul className="space-y-1">
                {content.callToActions.map((cta, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                    <ArrowRight className="w-3 h-3 text-primary" />
                    {formatListItem(cta)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Course Delivery Options - Show after all scripts are generated */}
        {isCourse && (
          <CourseDeliveryOptions
            content={content}
            lessonScripts={lessonScripts}
            product={product}
            project={project}
            onExport={handleCourseDeliveryExport}
            onContinue={handleContinue}
          />
        )}

        {/* Generate Sales Page Button */}
        <div className="glass-card p-6 border-primary/20 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold mb-1">Ready to sell?</h3>
              <p className="text-sm text-muted-foreground">Auto-generate a conversion-optimized sales page from this product.</p>
            </div>
            <Button
              variant="hero"
              onClick={() => {
                const params = new URLSearchParams({
                  title: product?.title || "",
                  type: product?.product_type || "",
                  benefits: content?.transformation || content?.positioning || "",
                });
                navigate(`/app/sales-page-builder?${params.toString()}`);
              }}
            >
              Generate Sales Page
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Export & Continue Buttons - Only show for non-courses OR if course scripts aren't all generated */}
        {(!isCourse || !content?.modules?.every((module: any, i: number) =>
          module.lessons?.every((_: any, j: number) => lessonScripts[`${i}-${j}`])
        )) && (
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportPDF}>
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <Button variant="outline" onClick={handleExportMarkdown}>
                <FileText className="w-4 h-4 mr-2" />
                Export Markdown
              </Button>
            </div>
            <Button variant="hero" size="lg" onClick={handleContinue}>
              Continue to Pricing
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
